import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from '@shopify/polaris';

// ── Bulk Print Labels (2026-08-27) ──────────────────────────────────────────
// Full-screen overlay launched from the Orders-list bulk bar. Flow:
//   1. POST /api/orders/bulk-labels/prepare  → eligible FO rows + excluded
//   2. per row: POST /api/orders/{id}/shipping-label/rates-all (3 concurrent)
//      — all UPS + USPS rates at the computed weight; weight edits re-rate
//      the SAME Shopify label session (debounced)
//   3. Buy & Print: sequential POST /shipping-label/purchase per row
//      (continue-on-failure, per-row Retry) — Shopify fulfills the FO and
//      schedules the customer email itself
//   4. open /api/shipping-labels/bulk-combined-pdf (one merged label+slip PDF)

const RATE_CONCURRENCY = 3;

const fmt$ = (n) => `$${Number(n).toFixed(2)}`;

function rowComputedTotal(row) {
  const sum = (row.items || []).reduce(
    (a, it) => a + (Number(row.itemWeights?.[it.id] ?? it.weight_lb) || 0) * (it.quantity || 1), 0);
  return Math.max(0.1, Math.round(sum * 100) / 100);
}

function rowTotal(row) {
  return row.totalOverride != null ? Math.max(0.1, Number(row.totalOverride)) : rowComputedTotal(row);
}

function pickDefaultRate(rates, prev, policy) {
  if (!rates || !rates.length) return null;
  if (prev) {
    const same = rates.find(r => r.carrier_code === prev.carrier_code && r.service_code === prev.service_code);
    if (same) return same;
  }
  // Bulk Shipping Rules (Settings tab) pin a ship-via per matching rule —
  // honor it exactly when the carrier offers that service for this shipment.
  // UPS Ground Saver is TWO service codes sharing one name — 92 under 1 lb,
  // 93 at 1 lb and over — so a rule pinning either accepts both (2026-09-01
  // user rule: Panini albums ship Ground Saver at ANY weight).
  if (policy?.carrier_code && policy?.service_code) {
    const wanted = String(policy.service_code);
    const family = (wanted === '92' || wanted === '93') ? ['92', '93'] : [wanted];
    const hit = rates.find(r => r.carrier_code === policy.carrier_code
      && String(r.service_code) === wanted)
      || rates.find(r => r.carrier_code === policy.carrier_code
        && family.includes(String(r.service_code)));
    if (hit) return hit;
  }
  // No rule matched (or the pinned service is unavailable): UPS Ground,
  // then cheapest UPS, then cheapest overall.
  const ground = rates.find(r => (r.carrier_code || '').startsWith('ups') && String(r.service_code) === '03');
  if (ground) return ground;
  const ups = rates.filter(r => (r.carrier_code || '').startsWith('ups'))
    .sort((x, y) => Number(x.price) - Number(y.price));
  if (ups.length) return ups[0];
  return [...rates].sort((x, y) => Number(x.price) - Number(y.price))[0];
}

// Carrier brand badges for the Ship-via dropdown (native <select> can't
// render images). Badge colors follow each carrier's identity.
function CarrierBadge({ carrier }) {
  const c = (carrier || '').toLowerCase();
  if (c.startsWith('ups')) {
    return <span className="blm-cbadge" style={{ background: '#351C15', color: '#FFB500' }}>UPS</span>;
  }
  if (c.startsWith('usps')) {
    return <span className="blm-cbadge" style={{ background: '#333366', color: '#fff' }}>USPS</span>;
  }
  if (c.startsWith('fedex')) {
    return <span className="blm-cbadge" style={{ background: '#fff', border: '1px solid #ddd' }}>
      <b style={{ color: '#4D148C' }}>Fed</b><b style={{ color: '#FF6600' }}>Ex</b></span>;
  }
  return <span className="blm-cbadge" style={{ background: '#e4e5e7', color: '#303030' }}>{(carrier || '?').slice(0, 4).toUpperCase()}</span>;
}

function RateDropdown({ rates, selected, disabled, onSelect }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const label = (rt) => `${(rt.service_name || rt.title || '').replace(/^\S*UPS\S*\s|^USPS\s|^FedEx\s/i, s0 => s0)} `;
  return (
    <div className="blm-ratedd" ref={ref}>
      <button type="button" className="blm-ratedd__btn" disabled={disabled}
              onClick={() => setOpen(v => !v)}>
        {selected ? (<>
          <CarrierBadge carrier={selected.carrier_code} />
          <span className="blm-ratedd__label">{selected.service_name || selected.title} · {fmt$(selected.price)}</span>
        </>) : <span className="blm-ratedd__label">Pick a rate…</span>}
        <span style={{ marginLeft: 'auto', color: '#8c9196' }}>▾</span>
      </button>
      {open && (
        <div className="blm-ratedd__menu">
          {rates.map(rt => (
            <button type="button" key={`${rt.carrier_code}|${rt.service_code}`}
                    className={`blm-ratedd__opt${selected && selected.carrier_code === rt.carrier_code && selected.service_code === rt.service_code ? ' blm-ratedd__opt--sel' : ''}`}
                    onClick={() => { onSelect(rt); setOpen(false); }}>
              <CarrierBadge carrier={rt.carrier_code} />
              <span className="blm-ratedd__label">{rt.service_name || rt.title}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 650 }}>{fmt$(rt.price)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const S = `
.blm-cbadge { display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; letter-spacing: 0.02em; border-radius: 4px; padding: 2px 5px; min-width: 34px; line-height: 1.2; flex: none; }
.blm-ratedd { position: relative; min-width: 250px; }
.blm-ratedd__btn { display: flex; align-items: center; gap: 8px; width: 100%; background: #fff; border: 1px solid #c9cccf; border-radius: 6px; padding: 4px 8px; font-size: 13px; cursor: pointer; white-space: nowrap; }
.blm-ratedd__btn:disabled { background: #f6f6f7; color: #8c9196; cursor: default; }
.blm-ratedd__label { white-space: nowrap; }
.blm-ratedd__menu { position: absolute; z-index: 40; top: calc(100% + 4px); left: 0; min-width: 300px; background: #fff; border: 1px solid #d9dbd5; border-radius: 8px; box-shadow: 0 6px 20px rgba(0,0,0,0.15); padding: 4px; max-height: 320px; overflow-y: auto; }
.blm-ratedd__opt { display: flex; align-items: center; gap: 8px; width: 100%; border: 0; background: none; padding: 6px 8px; font-size: 13px; cursor: pointer; border-radius: 6px; text-align: left; white-space: nowrap; }
.blm-ratedd__opt:hover { background: #f2f5ee; }
.blm-ratedd__opt--sel { background: #eef5fd; }
` + `
.blm-overlay { position: fixed; inset: 0; z-index: 1200; background: rgba(0,0,0,0.45); display: flex; align-items: stretch; justify-content: center; }
.blm-panel { background: #fff; margin: 16px; border-radius: 12px; flex: 1; max-width: min(1900px, calc(100vw - 32px)); display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 8px 40px rgba(0,0,0,0.3); }
.blm-head { display: flex; align-items: center; gap: 12px; padding: 14px 20px; border-bottom: 1px solid #e3e3e3; }
.blm-head h2 { margin: 0; font-size: 18px; font-weight: 650; }
.blm-head .blm-loc { color: #6d7175; font-size: 13px; }
.blm-close { margin-left: auto; border: 0; background: none; font-size: 22px; line-height: 1; cursor: pointer; color: #444; padding: 4px 8px; }
.blm-banner { background: #eaf4ff; border-bottom: 1px solid #d0e4f7; padding: 8px 20px; font-size: 13px; }
.blm-banner summary { cursor: pointer; font-weight: 600; }
.blm-banner ul { margin: 6px 0 2px 18px; padding: 0; }
.blm-body { flex: 1; overflow: auto; }
.blm-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.blm-table th { text-align: left; padding: 8px 10px; border-bottom: 1px solid #e3e3e3; background: #fafbfb; position: sticky; top: 0; z-index: 2; font-weight: 600; white-space: nowrap; }
.blm-table td { padding: 8px 10px; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
.blm-order-name { font-weight: 650; white-space: nowrap; }
.blm-sub { color: #6d7175; font-size: 12px; }
.blm-item { display: flex; align-items: center; gap: 8px; margin: 4px 0; }
.blm-item .t { flex: 1; min-width: 200px; line-height: 1.3; white-space: nowrap; }
.blm-item .t .blm-variant { display: inline-block; background: #e4e5e7; color: #303030; font-size: 11.5px; font-weight: 600; border-radius: 8px; padding: 1px 8px; margin-top: 3px; }
.blm-thumb { width: 34px; height: 34px; border-radius: 6px; border: 1px solid #e3e3e3; object-fit: cover; background: #f6f6f7; flex: none; }
.blm-thumb--empty { display: inline-flex; align-items: center; justify-content: center; color: #babec3; font-size: 16px; }
.blm-w-input { width: 62px; padding: 3px 6px; border: 1px solid #c9cccf; border-radius: 6px; font-size: 13px; text-align: right; }
.blm-w-input.blm-w--rule { background: #e7f7ec; border-color: #8fd6b1; }
.blm-total-input { width: 74px; padding: 4px 6px; border: 1px solid #c9cccf; border-radius: 6px; font-size: 13.5px; font-weight: 600; text-align: right; }
.blm-select { max-width: 250px; padding: 4px 6px; border: 1px solid #c9cccf; border-radius: 6px; font-size: 13px; }
.blm-price { font-weight: 650; white-space: nowrap; }
.blm-err { color: #b42318; font-size: 12.5px; max-width: 260px; }
.blm-status { white-space: nowrap; }
.blm-status-ok { color: #108043; font-weight: 650; white-space: nowrap; }
.blm-status-buying { color: #1a4fa0; white-space: nowrap; }
.blm-row--rule td { border-bottom: 0; padding-bottom: 2px; }
.blm-rule-row td { padding-top: 0; }
.blm-rule { display: inline-flex; align-items: center; gap: 6px; background: #e7f7ec; border: 1px solid #b7e4c7; color: #1b4332; border-radius: 6px; padding: 3px 10px; font-size: 12.5px; white-space: nowrap; }
.blm-foot { display: flex; align-items: center; gap: 14px; padding: 12px 20px; border-top: 1px solid #e3e3e3; background: #fafbfb; }
.blm-foot .blm-summary { font-size: 14px; }
.blm-foot .spacer { flex: 1; }
.blm-reset { border: 0; background: none; cursor: pointer; color: #2c6ecb; font-size: 12px; padding: 0; }
.blm-loading { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 60px; color: #6d7175; font-size: 15px; }
`;

export default function BulkLabelsModal({ open, onClose, orderIds, storeFetch, activeStore, urlShopifyLocationId = null }) {
  const [phase, setPhase] = useState('loading');  // loading | review | purchasing | done
  const [rows, setRows] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [locName, setLocName] = useState('');
  const [packages, setPackages] = useState([]);
  const [prepError, setPrepError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  // Post-purchase PDF progress: Shopify materializes label documents
  // asynchronously (seconds to minutes). We poll pdf-status with the bought
  // label tails and surface "X of Y ready" live (2026-09-01 user request).
  const [pdfProgress, setPdfProgress] = useState(null);  // {ready,total,statuses}
  const pdfPollRef = useRef(null);
  const startPdfPolling = useCallback((tails) => {
    clearInterval(pdfPollRef.current);
    if (!tails.length) return;
    setPdfProgress({ ready: 0, total: tails.length, statuses: {} });
    const startedAt = Date.now();
    const poll = async () => {
      try {
        const r = await storeFetch('/api/orders/bulk-labels/pdf-status', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label_gids: tails }),
        });
        const d = await r.json().catch(() => ({}));
        if (r.ok) {
          const invalid = d.invalid || 0;
          setPdfProgress({ ready: d.ready || 0, invalid, total: d.total || tails.length, statuses: d.statuses || {} });
          // 'invalid' = Shopify never produced a label for that purchase
          // (terminal). Stop polling once every label is ready or invalid.
          if ((d.ready || 0) + invalid >= (d.total || tails.length)) { clearInterval(pdfPollRef.current); return; }
        }
      } catch (e) { /* transient — keep polling */ }
      if (Date.now() - startedAt > 5 * 60 * 1000) clearInterval(pdfPollRef.current);
    };
    poll();
    pdfPollRef.current = setInterval(poll, 6000);
  }, [storeFetch]);
  useEffect(() => () => clearInterval(pdfPollRef.current), []);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const debounceRef = useRef({});
  const rateQueueRef = useRef({ active: 0, queue: [] });

  const setRow = useCallback((foGid, patch) => {
    setRows(prev => prev.map(r => r.fo_gid === foGid
      ? { ...r, ...(typeof patch === 'function' ? patch(r) : patch) } : r));
  }, []);

  // ── Rates fetch (queued, 3 concurrent) ───────────────────────────────────
  const fetchRates = useCallback(async (row) => {
    setRow(row.fo_gid, { ratesLoading: true, ratesError: null });
    // Batch rows re-rate through the batched FetchShippingRates op — never
    // through the legacy per-order session (would orphan the batch label).
    if (row.session?.batch && row.label_gid) {
      try {
        const r = await storeFetch('/api/orders/bulk-labels/batch-rerate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: row.order_id, fo_gid: row.fo_gid, label_gid: row.label_gid,
            package_key: row.packageKey || 'default',
            total_weight_lb: rowTotal(row),
            shopify_location_id: row.shopify_location_id,
          }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d?.detail || `Rate fetch failed (${r.status})`);
        setRow(row.fo_gid, prev => ({
          ratesLoading: false, rates: d.rates || [],
          selected: pickDefaultRate(d.rates || [], prev.selected, prev.rate_policy),
          ratesError: (d.rates || []).length ? null : 'No rates — check the shipping address.',
        }));
      } catch (e) {
        setRow(row.fo_gid, { ratesLoading: false, ratesError: String(e?.message || e) });
      }
      return;
    }
    try {
      const body = {
        fulfillment_order_id: row.fo_gid,
        total_weight_lb: rowTotal(row),
        item_weights: row.itemWeights || {},
        package_key: row.packageKey || 'default',
      };
      if (row.session?.label_gid) {
        body.label_gid = row.session.label_gid;
        body.session_gid = row.session.session_gid;
        body.session_uuid = row.session.session_uuid;
      }
      const r = await storeFetch(`/api/orders/${row.order_id}/shipping-label/rates-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail || `Rate fetch failed (${r.status})`);
      setRow(row.fo_gid, prev => ({
        ratesLoading: false,
        rates: d.rates || [],
        session: {
          label_gid: d.label_gid, session_gid: d.session_gid,
          session_uuid: d.session_uuid, shipping_date: d.shipping_date,
        },
        ratedWeight: d.total_weight_lb,
        selected: pickDefaultRate(d.rates || [], prev.selected, prev.rate_policy),
        ratesError: (d.rates || []).length ? null
          : 'No UPS/USPS rates — check the shipping address.',
      }));
    } catch (e) {
      setRow(row.fo_gid, { ratesLoading: false, ratesError: String(e?.message || e) });
    }
  }, [setRow, storeFetch]);

  const enqueueRates = useCallback((row) => {
    const q = rateQueueRef.current;
    const run = async (r0) => {
      q.active += 1;
      try { await fetchRates(r0); } finally {
        q.active -= 1;
        const next = q.queue.shift();
        if (next) run(next);
      }
    };
    if (q.active < RATE_CONCURRENCY) run(row);
    else q.queue.push(row);
  }, [fetchRates]);

  // ── Prepare on open ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setPhase('loading'); setRows([]); setExcluded([]); setPrepError(null); setPdfUrl(null);
    rateQueueRef.current = { active: 0, queue: [] };
    let dead = false;
    (async () => {
      try {
        const locBody = JSON.stringify({ order_ids: orderIds,
                                         shopify_location_id: (urlShopifyLocationId && urlShopifyLocationId !== 'all') ? urlShopifyLocationId : null });
        // Batch flow first (2026-09-01): ONE ShippingLabelsBulkCreate + ONE
        // batched rate fetch for the whole selection — rows arrive with the
        // full real-weight rate menu already attached. Falls back to the
        // legacy per-order prepare + rates-all path if the batch ops fail
        // (e.g. Shopify rotated the persisted-query hashes).
        let d = null, batch = false;
        try {
          const rb = await storeFetch('/api/orders/bulk-labels/batch-session', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: locBody,
          });
          const db = await rb.json().catch(() => ({}));
          if (rb.ok) { d = db; batch = true; }
          else if (rb.status === 400 || rb.status === 403) {
            throw new Error(db?.detail || `Prepare failed (${rb.status})`);
          }
        } catch (eb) {
          if (/location|Not authorized|caps at|No orders/.test(String(eb?.message))) throw eb;
          // batch unavailable — fall through to legacy
        }
        if (!d) {
          const r = await storeFetch('/api/orders/bulk-labels/prepare', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: locBody,
          });
          d = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(d?.detail || `Prepare failed (${r.status})`);
        }
        if (dead) return;
        const initRows = (d.rows || []).map(row => ({
          ...row,
          packageKey: row.default_package_key || 'default',
          itemWeights: {}, totalOverride: null,
          rates: batch ? (row.rates || []) : null,
          ratesLoading: false,
          ratesError: (batch && !(row.rates || []).length)
            ? 'No rates — check the shipping address.' : null,
          session: batch ? { label_gid: row.label_gid, batch: true } : null,
          selected: batch ? pickDefaultRate(row.rates || [], null, row.rate_policy) : null,
          status: null, error: null, purchased: null,
        }));
        setRows(initRows);
        setExcluded(d.excluded || []);
        setPackages(d.packages || []);
        setLocName(d.location_name || '');
        setPhase('review');
        if (!batch) initRows.forEach(row => enqueueRates(row));
      } catch (e) {
        if (!dead) { setPrepError(String(e?.message || e)); setPhase('review'); }
      }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Weight edits (debounced re-rate) ─────────────────────────────────────
  const scheduleRerate = useCallback((foGid) => {
    clearTimeout(debounceRef.current[foGid]);
    debounceRef.current[foGid] = setTimeout(() => {
      const row = rowsRef.current.find(r => r.fo_gid === foGid);
      if (row && row.status !== 'ok') enqueueRates(row);
    }, 900);
  }, [enqueueRates]);

  const onItemWeight = (foGid, liGid, val) => {
    setRow(foGid, prev => ({
      itemWeights: { ...prev.itemWeights, [liGid]: val },
      totalOverride: null,
    }));
    if (val !== '' && !isNaN(Number(val))) scheduleRerate(foGid);
  };

  const onTotalWeight = (foGid, val) => {
    setRow(foGid, { totalOverride: val === '' ? null : val });
    if (val !== '' && !isNaN(Number(val))) scheduleRerate(foGid);
  };

  const onPackage = (foGid, key) => {
    setRow(foGid, { packageKey: key });
    scheduleRerate(foGid);
  };

  // ── Purchase loop ────────────────────────────────────────────────────────
  const buyableRows = useMemo(
    () => rows.filter(r => r.selected && !r.ratesError && r.status !== 'ok'),
    [rows]);
  const anyLoading = rows.some(r => r.ratesLoading);
  const totalCost = buyableRows.reduce((a, r) => a + Number(r.selected?.price || 0), 0);

  // `win` is a tab opened synchronously inside the click gesture (popup
  // blockers kill window.open after long awaits) — we navigate it once the
  // URL is ready. Falls back to window.open when no pre-opened tab exists.
  const openBulkPdf = useCallback(async (tails, win) => {
    try {
      const r = await storeFetch('/api/label-token', { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      const token = d?.token || '';
      const url = `/api/shipping-labels/bulk-combined-pdf?tails=${encodeURIComponent(tails.join(','))}`
        + `&store=${encodeURIComponent(activeStore || 'soccerpost')}&token=${encodeURIComponent(token)}`;
      setPdfUrl(url);
      if (win && !win.closed) win.location = url;
      else window.open(url, '_blank');
    } catch (e) {
      if (win && !win.closed) win.close();
      setPdfUrl(null);
    }
  }, [storeFetch, activeStore]);

  // Blank tab + holding message, opened inside the user's click.
  const openHoldingTab = () => {
    const w = window.open('', '_blank');
    try {
      if (w) {
        w.document.title = 'Preparing labels…';
        const body = w.document.body;
        body.style.cssText = 'font-family:-apple-system,Helvetica,Arial,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#1a4fa0;background:#f3f4f5';
        const box = w.document.createElement('div');
        box.style.textAlign = 'center';
        const h = w.document.createElement('div');
        h.style.cssText = 'font-size:20px;font-weight:600';
        h.textContent = 'Purchasing labels…';
        const sub = w.document.createElement('div');
        sub.style.cssText = 'color:#6b7280;margin-top:8px';
        sub.textContent = 'This tab will load the label PDFs when they are ready. Shopify can take up to a minute to generate them.';
        box.appendChild(h); box.appendChild(sub);
        body.appendChild(box);
      }
    } catch (e) { /* cross-origin after nav — ignore */ }
    return w;
  };

  const purchaseOne = useCallback(async (row) => {
    setRow(row.fo_gid, { status: 'buying', error: null });
    try {
      const r = await storeFetch(`/api/orders/${row.order_id}/shipping-label/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fulfillment_order_id: row.fo_gid,
          label_gid: row.session.label_gid,
          session_gid: row.session.session_gid,
          quoted_price: String(row.selected.price),
          shipping_date: row.session.shipping_date,
          carrier_code: row.selected.carrier_code,
          service_code: row.selected.service_code,
          total_weight_lb: rowTotal(row),
          item_weights: row.itemWeights || {},
          package_key: row.packageKey || 'default',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail || `Purchase failed (${r.status})`);
      setRow(row.fo_gid, { status: 'ok', purchased: d });
      return d;
    } catch (e) {
      setRow(row.fo_gid, { status: 'error', error: String(e?.message || e) });
      return null;
    }
  }, [setRow, storeFetch]);

  // ONE ShippingLabelsPurchase for the whole batch (2026-09-01). Returns
  // the purchased label tails; per-label errors land on their rows.
  const purchaseBatch = useCallback(async (batchTargets) => {
    if (!batchTargets.length) return [];
    batchTargets.forEach(r0 => setRow(r0.fo_gid, { status: 'buying', error: null }));
    const bought = [];
    try {
      const r = await storeFetch('/api/orders/bulk-labels/batch-purchase', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ labels: batchTargets.map(row => ({
          label_gid: row.label_gid, order_id: row.order_id, fo_gid: row.fo_gid,
          package_key: row.packageKey || 'default',
          total_weight_lb: rowTotal(row),
          carrier_code: row.selected.carrier_code,
          service_code: row.selected.service_code,
          service_name: row.selected.service_name || row.selected.title || '',
          quoted_price: String(row.selected.price),
        })) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail || `Batch purchase failed (${r.status})`);
      for (const res of (d.results || [])) {
        const row = batchTargets.find(x => x.label_gid === res.label_gid
          || String(x.order_id) === String(res.order_id));
        if (!row) continue;
        if (res.ok) {
          setRow(row.fo_gid, { status: 'ok', purchased: { label_gid: res.label_gid } });
          bought.push(String(res.label_gid).split('/').pop());
        } else {
          setRow(row.fo_gid, { status: 'error', error: res.error || 'Purchase failed' });
        }
      }
    } catch (e) {
      batchTargets.forEach(r0 => setRow(r0.fo_gid, { status: 'error', error: String(e?.message || e) }));
    }
    return bought;
  }, [setRow, storeFetch]);

  const handleBuyAll = useCallback(async () => {
    // Snapshot prior successes BEFORE the loop; collect new ones locally.
    // (Reading rowsRef right after purchaseOne is a race — React may not
    // have flushed the status updates yet, which silently produced an
    // empty tail list and no PDF tab on the first live purchase.)
    const prior = rowsRef.current
      .filter(r => r.status === 'ok' && r.purchased?.label_gid)
      .map(r => String(r.purchased.label_gid).split('/').pop());
    const win = openHoldingTab();
    setPhase('purchasing');
    const targets = rowsRef.current.filter(r => r.selected && !r.ratesError && r.status !== 'ok');
    const batchTargets = targets.filter(r => r.session?.batch && r.label_gid);
    const legacyTargets = targets.filter(r => !(r.session?.batch && r.label_gid));
    const bought = await purchaseBatch(batchTargets);
    for (const row of legacyTargets) {
      const d = await purchaseOne(row);
      if (d?.label_gid) bought.push(String(d.label_gid).split('/').pop());
    }
    const allTails = Array.from(new Set([...prior, ...bought]));
    setPhase('done');
    startPdfPolling(allTails);
    if (allTails.length) await openBulkPdf(allTails, win);
    else if (win && !win.closed) win.close();
  }, [purchaseOne, purchaseBatch, openBulkPdf, startPdfPolling]);

  const retryRow = useCallback(async (row) => {
    const prior = rowsRef.current
      .filter(r => r.status === 'ok' && r.purchased?.label_gid)
      .map(r => String(r.purchased.label_gid).split('/').pop());
    const win = openHoldingTab();
    const isBatch = !!(row.session?.batch && row.label_gid);
    // Batch labels are reusable (purchase dedups via alreadyPurchased);
    // legacy retries need a fresh session — the failed one may be burned.
    setRow(row.fo_gid, isBatch
      ? { rates: null, selected: null, status: null, error: null }
      : { session: null, rates: null, selected: null, status: null, error: null });
    const fresh = isBatch ? { ...row } : { ...row, session: null };
    await fetchRates(fresh);
    const after = rowsRef.current.find(r => r.fo_gid === row.fo_gid);
    let boughtTails = [];
    if (after?.selected && !after.ratesError) {
      if (isBatch) {
        boughtTails = await purchaseBatch([after]);
      } else {
        const d = await purchaseOne(after);
        if (d?.label_gid) boughtTails = [String(d.label_gid).split('/').pop()];
      }
    }
    const allTails = Array.from(new Set([...prior, ...boughtTails]));
    if (allTails.length) await openBulkPdf(allTails, win);
    else if (win && !win.closed) win.close();
  }, [setRow, fetchRates, purchaseOne, purchaseBatch, openBulkPdf]);

  if (!open) return null;
  const purchasing = phase === 'purchasing';
  const doneCount = rows.filter(r => r.status === 'ok').length;
  const failCount = rows.filter(r => r.status === 'error').length;

  return (
    <div className="blm-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !purchasing) onClose(); }}>
      <style>{S}</style>
      <div className="blm-panel">
        <div className="blm-head">
          <h2>Bulk Print Labels</h2>
          {locName ? <span className="blm-loc">from {locName}</span> : null}
          <button className="blm-close" onClick={() => { if (!purchasing) onClose(); }} title="Close">✕</button>
        </div>

        {excluded.length > 0 && (
          <div className="blm-banner">
            <details>
              <summary>{excluded.length} order{excluded.length === 1 ? ' is' : 's are'} excluded — not eligible for shipping labels</summary>
              <ul>
                {excluded.map(x => (
                  <li key={x.order_id}><b>{x.order_name || x.order_id}</b> — {x.reason}</li>
                ))}
              </ul>
            </details>
          </div>
        )}

        <div className="blm-body">
          {phase === 'loading' ? (
            <div className="blm-loading"><Spinner size="small" /> Checking {orderIds.length} selected order{orderIds.length === 1 ? '' : 's'}…</div>
          ) : prepError ? (
            <div className="blm-loading blm-err">{prepError}</div>
          ) : rows.length === 0 ? (
            <div className="blm-loading">None of the selected orders are eligible for shipping labels.</div>
          ) : (
            <table className="blm-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Items · weight (lb each)</th>
                  <th>Total weight</th>
                  <th>Package</th>
                  <th>Customer chose</th>
                  <th>Ship via</th>
                  <th>Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const total = rowTotal(row);
                  const overridden = row.totalOverride != null;
                  return (
                    <React.Fragment key={row.fo_gid}>
                    <tr className={row.rule_name ? 'blm-row--rule' : undefined}>
                      <td>
                        <div className="blm-order-name">{row.order_name}</div>
                        <div className="blm-sub">{row.customer_name}</div>
                        <div className="blm-sub">{row.destination}</div>
                      </td>
                      <td>
                        {row.items.map(it => (
                          <div className="blm-item" key={it.id}>
                            {it.image_url
                              ? <img className="blm-thumb" src={`${it.image_url}${it.image_url.includes('?') ? '&' : '?'}width=68`} alt="" loading="lazy" />
                              : <span className="blm-thumb blm-thumb--empty">🖼</span>}
                            <span className="t">
                              <div>{it.quantity > 1 ? `${it.quantity} × ` : ''}{it.title}</div>
                              {it.variant_title && it.variant_title !== 'Default Title' && (
                                <div className="blm-variant">{it.variant_title}</div>
                              )}
                            </span>
                            <input
                              className={`blm-w-input${it.weight_source === 'shoe_rule' ? ' blm-w--rule' : ''}`}
                              type="number" min="0" step="0.1"
                              disabled={purchasing || row.status === 'ok'}
                              value={row.itemWeights[it.id] ?? it.weight_lb}
                              onChange={e => onItemWeight(row.fo_gid, it.id, e.target.value)}
                              title={it.weight_source === 'shoe_rule' ? 'Default from the small-shoe rule (size ≤ 6 → 1 lb)'
                                : it.weight_source === 'fallback' ? 'No variant weight — hardcoded fallback' : 'Shopify variant weight'}
                            />
                          </div>
                        ))}
                      </td>
                      <td>
                        <input
                          className="blm-total-input" type="number" min="0.1" step="1"
                          disabled={purchasing || row.status === 'ok'}
                          value={row.totalOverride ?? rowComputedTotal(row)}
                          onChange={e => onTotalWeight(row.fo_gid, e.target.value)}
                        />
                        {overridden && (
                          <div><button className="blm-reset" onClick={() => onTotalWeight(row.fo_gid, '')}>↺ use item sum</button></div>
                        )}
                      </td>
                      <td>
                        <select
                          className="blm-select"
                          style={{ maxWidth: 190 }}
                          disabled={purchasing || row.status === 'ok'}
                          value={row.packageKey || 'default'}
                          onChange={e => onPackage(row.fo_gid, e.target.value)}
                        >
                          {(packages.length ? packages : [{ key: 'default', name: 'Store default', dims: '' }]).map(p => (
                            <option key={p.key} value={p.key}>{p.name}{p.dims ? ` · ${p.dims}` : ''}</option>
                          ))}
                        </select>
                      </td>
                      <td><div className="blm-sub" style={{ fontSize: 13, color: '#333', whiteSpace: 'nowrap' }}>{row.shipping_line_title || '—'}</div></td>
                      <td>
                        {row.ratesLoading ? (
                          <span className="blm-sub"><Spinner size="small" /> fetching rates…</span>
                        ) : row.ratesError ? (
                          <div className="blm-err">{row.ratesError}</div>
                        ) : row.rates ? (
                          <RateDropdown
                            rates={row.rates}
                            selected={row.selected}
                            disabled={purchasing || row.status === 'ok'}
                            onSelect={rt => setRow(row.fo_gid, { selected: rt })}
                          />
                        ) : <span className="blm-sub">queued…</span>}
                        {row.ratedWeight != null && !row.ratesLoading && Math.abs(row.ratedWeight - total) > 0.01 && row.status !== 'ok' && (
                          <div className="blm-sub" style={{ color: '#916a00' }}>re-rating at {total} lb…</div>
                        )}
                      </td>
                      <td className="blm-price">{row.selected && !row.ratesError ? fmt$(row.selected.price) : '—'}</td>
                      <td className="blm-status">
                        {row.status === 'ok' ? (
                          <span className="blm-status-ok">✓ Purchased{(() => {
                            const t = row.purchased?.label_gid ? String(row.purchased.label_gid).split('/').pop() : null;
                            const st = t && pdfProgress?.statuses ? pdfProgress.statuses[t] : null;
                            if (st === 'ready') return ' · PDF ✓';
                            if (st === 'pending') return ' · PDF ⏳';
                            if (st === 'invalid') return ' · no label — buy again from Order Detail';
                            return '';
                          })()}</span>
                        )
                          : row.status === 'buying' ? <span className="blm-status-buying"><Spinner size="small" /> buying…</span>
                          : row.status === 'error' ? (
                            <div>
                              <div className="blm-err">{row.error}</div>
                              <Button size="micro" onClick={() => retryRow(row)}>Retry</Button>
                            </div>
                          ) : null}
                      </td>
                    </tr>
                    {row.rule_name ? (
                      <tr className="blm-rule-row">
                        <td />
                        <td colSpan={7}>
                          <span className="blm-rule" title="Bulk Shipping Rule applied">📏 {row.rule_name}</span>
                        </td>
                      </tr>
                    ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="blm-foot">
          <div className="blm-summary">
            {phase === 'done'
              ? <>
                  Purchased <b>{doneCount}</b> label{doneCount === 1 ? '' : 's'}{failCount ? <> · <span className="blm-err" style={{ display: 'inline' }}>{failCount} failed</span></> : null}
                  {pdfProgress && (
                    pdfProgress.ready + (pdfProgress.invalid || 0) >= pdfProgress.total
                      ? (pdfProgress.invalid
                          ? <span style={{ color: '#b26a00', fontWeight: 650 }}> · {pdfProgress.ready} of {pdfProgress.total} PDFs ready — {pdfProgress.invalid} purchase{pdfProgress.invalid === 1 ? '' : 's'} did not produce a label (see rows); the print tab has the rest</span>
                          : <span style={{ color: '#108043', fontWeight: 650 }}> · All {pdfProgress.total} PDFs ready ✓ — printing tab is good to go</span>)
                      : <span style={{ color: '#1a4fa0' }}> · <Spinner size="small" /> Preparing PDFs: <b>{pdfProgress.ready}</b> of {pdfProgress.total} ready — the print tab loads when all are done</span>
                  )}
                </>
              : <><b>{buyableRows.length}</b> label{buyableRows.length === 1 ? '' : 's'} ready{anyLoading ? ' (rates still loading…)' : ''}</>}
          </div>
          <div className="spacer" />
          {phase === 'done' && pdfUrl && (
            <Button onClick={() => window.open(pdfUrl, '_blank')}>Reopen PDF</Button>
          )}
          <Button onClick={onClose} disabled={purchasing}>Close</Button>
          {phase !== 'done' && (
            <Button
              variant="primary"
              disabled={purchasing || anyLoading || buyableRows.length === 0}
              loading={purchasing}
              onClick={handleBuyAll}
            >
              {`Buy & Print ${buyableRows.length} label${buyableRows.length === 1 ? '' : 's'} · ${fmt$(totalCost)}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
